import React, { useContext } from 'react'
import { useEffect, useState } from 'react'
import { Button, Col, ComboBoxDefaultOption, Container, Inline, Modal, Row } from '@airbus/components-react/components'
import { useTranslation } from 'react-i18next'
import PageHeader from 'components/pageHeader'
import { DisplayModus } from 'constants/constants'
import useMutation from 'utils/hooks/useMutation'
import URL_APIS from 'utils/api/urlApis'
import ModalConfirm from 'components/Modal/ModalConfim'
import { FormProvider, useForm } from 'react-hook-form'
import FormSelect from 'components/FormFields/FormSelect'
import useOptions from 'utils/hooks/useOptions'
import FormInput from 'components/FormFields/FormInput'
import { Lang } from 'context/LangContext'
import LightDarkContext from 'context/LightDarkModeContext'
import { FuelDetailInput } from 'types/techlog'
import FormNumericInput from 'components/FormFields/FormNumericInput'

const DEFAULT_CODE_LENGTH = 8
export const renderOptionLabel = (o: ComboBoxDefaultOption, codeLength: number = DEFAULT_CODE_LENGTH) => o?.label

type Props = {
  lang: Lang
  open: boolean
  displayMode: DisplayModus
  onClose: () => void
  techlogFuel?: FuelDetailInput
  onPostSubmit: () => void
}

const styleModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: '2px solid #000',
  boxShadow: 24,
  height: '400px',
  width: '60%',
  overflow: 'hidden',
}

const ModalTechlogFuel = ({ lang, open, displayMode, onClose, techlogFuel, onPostSubmit }: Props) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { loadOptions: loadOptionsFuelTypes, options: fuelTypeOptions } = useOptions(URL_APIS.URL_CRM_FUEL_TYPES_GET)

  const { mutate: createOrUpdateFlightFuel } = useMutation<FuelDetailInput>(URL_APIS.URL_CRM_FUEL)

  const methods = useForm<FuelDetailInput>({
    values: techlogFuel,
  })

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = methods

  useEffect(() => {
    loadOptionsFuelTypes({ lang: lang, column: 'A' }, 'ketycod', 'ketylib', 'ketylib', true, false)
  }, [])

  const onSubmit = async (data: FuelDetailInput, event: React.BaseSyntheticEvent) => {
    event.stopPropagation()
    setIsLoading(true)
    await createOrUpdateFlightFuel({
      data: {
        ...data,
        fuelType: data.fuelTypeCode,
        rmdate: new Date(data.rmdate[0], data.rmdate[1] - 1, data.rmdate[2], 23, 59),
        kebon: data.kebon && data.kebon.toUpperCase(),
      },
      params: { lang },
      method: displayMode === DisplayModus.CREATE ? 'POST' : 'PUT',
    })
    reset()
    onPostSubmit()
    setIsLoading(false)
  }

  const handleClose = () => {
    setConfirmOpen(false)
    reset()
    onClose()
  }

  const { getBackgroundColor } = useContext(LightDarkContext)

  const handleCancel = () => {
    if (methods.formState.isDirty) {
      setConfirmOpen(true)
    } else {
      reset()
      onClose()
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
          <PageHeader
            pageTitle={t('7002402')}
            modalEmbedded={true}
            modalOnClose={onClose}
            style={{ top: 0 }}
            localLoading={isLoading}
          />
          <Container style={{ flex: 1 }} fixed>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div style={{ margin: '20px 50px' }}>
                  <Row justify='space-between'>
                    <Col md={4} xs={12}>
                      <FormSelect
                        label={t('1P00012')}
                        name='fuelTypeCode'
                        options={fuelTypeOptions}
                        rules={{
                          required: true,
                        }}
                        error={Boolean(errors?.fuelTypeCode)}
                        getOptionLabel={renderOptionLabel}
                      />
                    </Col>
                    <Col md={3} xs={12}>
                      <FormNumericInput
                        label={t('7000199')}
                        name='keqte'
                        placeholder={t('placeholder_techlogs_oil_qty')}
                        rules={{
                          required: true,
                        }}
                        minValue={-9999999}
                        maxValue={9999999}
                        step={1}
                        error={Boolean(errors.keqte)}
                      />
                    </Col>
                    <Col md={3} xs={12}>
                      <FormInput
                        label={t('1MRM069')}
                        name='kebon'
                        placeholder={t('1MRM069') as string}
                        rules={{
                          required: true,
                        }}
                        error={Boolean(errors?.kebon)}
                        capitalize
                      />
                    </Col>
                  </Row>
                </div>
                <Inline
                  align='space-around'
                  style={{
                    padding: '10px',
                    position: 'absolute',
                    bottom: 0,
                    marginTop: '1em',
                    width: '100%',
                  }}
                >
                  <Button onClick={handleCancel} variant='secondary'>
                    {t('8000569')}
                  </Button>
                  <Button type='submit' variant='primary' disabled={!methods.formState.isDirty && isLoading}>
                    {t('9500174')}
                  </Button>
                </Inline>
              </form>
            </FormProvider>
          </Container>
        </div>
      </Modal>
      <ModalConfirm
        confirmMessage={t('GEN0090')}
        isOpened={confirmOpen}
        onCloseModal={() => setConfirmOpen(false)}
        onSubmit={handleClose}
      />
    </>
  )
}

export default ModalTechlogFuel
